"use client";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Trash2, UserPlus } from "lucide-react";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { CompanyMember } from "../../api/companiesApi";
import type { KnowledgeGrant, KnowledgeGrantRelation } from "../../api/knowledgeGrantsApi";

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: number;
  documentLabel: string;
}

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Share a single document with company members: pick a member, choose
 *  viewer/editor, grant. Lists current grantees with a revoke action.
 *  Mirrors CreateEmbedKeyDialog's structure + CompanyMembers' member list. */
export function ShareDocumentDialog({ open, onClose, documentId, documentLabel }: Props) {
  const { companies, knowledgeGrants } = useAuthedApi();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [grants, setGrants] = useState<KnowledgeGrant[] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [relation, setRelation] = useState<KnowledgeGrantRelation>("viewer");
  const [busy, setBusy] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, g] = await Promise.all([
        companies.membersAll(),
        knowledgeGrants.listGrants(documentId),
      ]);
      setMembers(m);
      setGrants(g);
    } catch (e) {
      setError(message(e));
      setGrants([]);
    }
  }, [companies, knowledgeGrants, documentId]);

  useEffect(() => {
    if (!open) return;
    setSelectedUserId("");
    setRelation("viewer");
    void load();
  }, [open, load]);

  const onGrant = async () => {
    if (!selectedUserId) return;
    setBusy(true);
    setError(null);
    try {
      await knowledgeGrants.addGrant(documentId, selectedUserId, relation);
      setSelectedUserId("");
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (userId: string, rel: KnowledgeGrantRelation) => {
    setRevokingUserId(userId);
    setError(null);
    try {
      await knowledgeGrants.removeGrant(documentId, userId, rel);
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setRevokingUserId(null);
    }
  };

  const memberLabel = (userId: string) =>
    members.find((m) => m.user_id === userId)?.email ?? userId;

  // A member already granted shouldn't also show up in the picker.
  const grantedUserIds = new Set((grants ?? []).map((g) => g.user_id));
  const pickable = members.filter((m) => !grantedUserIds.has(m.user_id));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share &quot;{documentLabel}&quot;</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={1}>
            <TextField
              select
              label="Member"
              size="small"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              sx={{ flex: 1 }}
              disabled={pickable.length === 0}
            >
              {pickable.map((m) => (
                <MenuItem key={m.user_id} value={m.user_id}>
                  {m.email ?? m.user_id}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Access"
              size="small"
              value={relation}
              onChange={(e) => setRelation(e.target.value as KnowledgeGrantRelation)}
              sx={{ width: 140 }}
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
            </TextField>
            <Button
              variant="contained"
              startIcon={busy ? <CircularProgress size={16} /> : <UserPlus size={16} />}
              onClick={onGrant}
              disabled={busy || !selectedUserId}
            >
              Share
            </Button>
          </Stack>

          <Divider textAlign="left">
            <Typography variant="caption">People with access</Typography>
          </Divider>

          {grants === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : grants.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No one has been granted access yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {grants.map((g) => (
                <Box
                  key={`${g.user_id}:${g.relation}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {memberLabel(g.user_id)}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      {g.relation}
                    </Typography>
                    <Tooltip title="Revoke access">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`revoke ${memberLabel(g.user_id)}`}
                          disabled={revokingUserId === g.user_id}
                          onClick={() => onRevoke(g.user_id, g.relation)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
